import React, { useState, useEffect } from 'react';
import { Head } from 'vite-react-ssg';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Target, Award, Leaf, Plus, Minus, X } from 'lucide-react';
import Preloader from '../components/Preloader';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import CustomCursor from '../components/CustomCursor';
import Slider from '../components/Slider';
import Trustmeets from '@/components/Trustmeets';
import MarqueeSection from '@/components/MarqueeSection';
import TestimonialHeading from '@/components/TestimonialHeading';

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showEventPopup, setShowEventPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  // Show the event popup shortly after the preloader finishes
  useEffect(() => {
    if (!loading) {
      const popupTimer = setTimeout(() => {
        setShowEventPopup(true);
      }, 600);
      return () => clearTimeout(popupTimer);
    }
  }, [loading]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handlePopupClick = () => {
    setShowEventPopup(false);
    navigate('/events');
  };

  const closePopup = (e) => {
    e.stopPropagation();
    setShowEventPopup(false);
  };

  return (
    <div className={darkMode ? 'dark' : ''}>

<Head>
  {/* ── Basic Meta ── */}
  <title>VibrantViva | Legal Direct Selling and Business Opportunity India</title>
  <meta
    name="description"
    content="Join VibrantViva and explore a transparent direct selling business model. Build entrepreneurial skills, grow your network, and create long-term opportunities."
  />
  <meta
    name="keywords"
    content="Direct Selling Company India, Legal Direct Selling, Network Marketing India, MLM Business Opportunity, Entrepreneur Opportunity, Passive Income Opportunity, Business Opportunity India, best direct selling company in India, legal network marketing company, direct selling business opportunity, Entrepreneurship Development, Entrepreneur Success Platform, VibrantViva Business Opportunity, Entrepreneurship, Entrepreneur Network, long term growth opportunity, Success Opportunity"
  />
  <meta name="author" content="VibrantViva" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://vibrantviva.com/" />

  {/* ── Open Graph (Facebook / WhatsApp / LinkedIn) ── */}
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://vibrantviva.com/" />
  <meta
    property="og:title"
    content="VibrantViva | Legal Direct Selling & Business Opportunity India"
  />
  <meta
    property="og:description"
    content="Join VibrantViva and explore a transparent direct selling business model. Build entrepreneurial skills, grow your network, and create long-term opportunities."
  />
  <meta property="og:image" content="https://vibrantviva.com/assets/logo.png" />
  <meta property="og:image:width" content="200" />
  <meta property="og:image:height" content="200" />
  <meta property="og:site_name" content="VibrantViva" />
  <meta property="og:locale" content="en_IN" />

  {/* ── JSON-LD Structured Data ── */}
  <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "VibrantViva",
      "url": "https://vibrantviva.com/",
      "logo": "https://vibrantviva.com/assets/logo.png",
      "description":
        "VibrantViva is a legal direct selling company in India offering transparent business opportunities, entrepreneurial growth, leadership development, and long-term income potential.",
      "sameAs": [
        "https://www.facebook.com/media/set/?vanity=officialVibrantViva&set=a.705168821617957",
        "https://www.instagram.com/vibrantvivaofficial/",
        "https://www.linkedin.com/company/indusviva-healthsciences-private-limited/posts/?feedView=all&viewAsMember=true",
        "https://x.com/IndusvivaPvt?t=yf_P18W0HW6DR9ZbrhjRzg&s=09",
        "https://www.youtube.com/@VibrantViva"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-1800-103-4916",
        "contactType": "Customer Support",
        "hoursAvailable": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:30",
            "closes": "18:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Saturday"],
            "opens": "09:30",
            "closes": "13:30"
          }
        ],
        "availableLanguage": [
          "English",
          "Hindi",
          "Kannada",
          "Tamil",
          "Telugu",
          "Malayalam"
        ],
        "areaServed": "IN",
        "contactOption": "TollFree"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
        "addressLocality": "Bangalore Urban",
        "addressRegion": "Karnataka",
        "postalCode": "560046",
        "addressCountry": "IN"
      },
      "location": {
        "@type": "Place",
        "name": "Viva Tower",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
          "addressLocality": "Bangalore Urban",
          "addressRegion": "Karnataka",
          "postalCode": "560046",
          "addressCountry": "IN"
        }
      }
    })}
  }
  />
</Head>

      <Preloader loading={loading} />
      <CustomCursor />
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      {showEventPopup && (
        <div
          onClick={() => setShowEventPopup(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        >
          <div
            style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closePopup}
              aria-label="Close popup"
              style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                background: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              <X size={18} />
            </button>
            <img
              src="/assets/evnt_popup.jpeg"
              alt="Upcoming Event"
              onClick={handlePopupClick}
              style={{
                maxWidth: '87vw',
                maxHeight: '65vh',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'block',
              }}
            />
          </div>
        </div>
      )}

      <Hero />
      <Slider />
      <Trustmeets />
      <MarqueeSection />
      <TestimonialHeading />
      <Footer />
      <BackToTop />
    </div>
  );
};

export default Home;